import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import { Tooltip as ReactTooltip } from "react-tooltip";

import {
  Club,
  GlobalWrap,
  HeaderLabelText,
  PlayerColumnHeaderContent,
  PlayerColumnRowContent,
  PlayerFavoriteIcon,
  PlayerMediaContent,
  PlayerMediaPlaceholder,
  PlayerNameText,
  PlayerNumberText,
  PlayerPhoto,
  PlayerPositionText,
  PlayerStatsLoadingState,
  PlayerStatsScrollContainer,
  PlayerStatsSingleTable,
  PlayerTable,
  StatHeaderLabel,
} from "../../style/PlayersStat";
import placeholder from "../../images/ic_logo.svg";
import starOn from "../../images/star_on.svg";
import starOff from "../../images/star_off.svg";
import { ReactComponent as SortAscIcon } from "../../images/icons/sortasc_icon.svg";
import { ReactComponent as SortDescIcon } from "../../images/icons/sort_desc.svg";
import { ReactComponent as SortDefaultIcon } from "../../images/icons/sort_default.svg";
import { $apiV1 } from "../../config/api";
import { useLexicon } from "../../hooks/useLexicon";
import { extractFirstCharacters } from "../../helpers";
import { openVideoList } from "../../helpers/service";

const getDefaultParameterDisplayName = (parameter) =>
  parameter?.parameter_name || parameter?.name || parameter?.title || "";

const getVisibleStatsMeta = (players = [], selectedIds = []) => {
  const statsById = new Map();

  (players || []).forEach((player) => {
    (player?.stats || []).forEach((stat) => {
      if (!statsById.has(stat.parameter_id)) {
        statsById.set(stat.parameter_id, stat);
      }
    });
  });

  const availableIds = [...statsById.keys()];

  if (availableIds.length === 0) {
    return [];
  }

  const matchingIds = selectedIds?.length
    ? availableIds.filter((id) => selectedIds.includes(id))
    : availableIds;

  const finalIds = matchingIds.length > 0 ? matchingIds : availableIds;

  return finalIds
    .sort((a, b) => a - b)
    .map((id) => statsById.get(id))
    .filter(Boolean);
};

const formatStatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  if (typeof value === "number") {
    return value.toFixed(1);
  }

  return value;
};

const PlayersStat = ({
  items,
  club,
  tempSelectedFilters,
  playerSelectedFilters,
  loading = false,
  getParameterDisplayName = getDefaultParameterDisplayName,
}) => {
  const l = useLexicon();
  const navigate = useNavigate();
  const [favoritePlayers, setFavoritePlayers] = useState({});
  const [tableTypeSortingHome, setTableTypeSortingHome] = useState({
    type: "default",
    param: "",
  });
  const [tableTypeSortingAway, setTableTypeSortingAway] = useState({
    type: "default",
    param: "",
  });

  const filteredHomeTeam = useMemo(
    () => items?.home_team?.filter((player) => !player.positions?.includes("GK")),
    [items?.home_team]
  );
  const filteredAwayTeam = useMemo(
    () => items?.away_team?.filter((player) => !player.positions?.includes("GK")),
    [items?.away_team]
  );

  const visibleStatsMeta = useMemo(
    () =>
      getVisibleStatsMeta(
        [...(filteredHomeTeam || []), ...(filteredAwayTeam || [])],
        playerSelectedFilters?.checkedIds
      ),
    [filteredHomeTeam, filteredAwayTeam, playerSelectedFilters?.checkedIds]
  );

  // parameter_id -> stat, indexed per player id. Building this once turns every
  // stat-cell lookup (and sort comparison) into O(1) instead of a linear .find()
  // over player.stats, which was O(players x params x stats) per render.
  const statByPlayerId = useMemo(() => {
    const byPlayer = new Map();
    const indexPlayer = (player) => {
      if (!player) return;
      const statMap = new Map();
      (player.stats || []).forEach((stat) => {
        if (!statMap.has(stat.parameter_id)) {
          statMap.set(stat.parameter_id, stat);
        }
      });
      byPlayer.set(player.id, statMap);
    };
    (filteredHomeTeam || []).forEach(indexPlayer);
    (filteredAwayTeam || []).forEach(indexPlayer);
    return byPlayer;
  }, [filteredHomeTeam, filteredAwayTeam]);

  const getStat = useCallback(
    (player, parameterId) => statByPlayerId.get(player?.id)?.get(parameterId),
    [statByPlayerId]
  );

  // Progressive row rendering. The grid is players x all-parameters and both team
  // tables mount at once, so building every row synchronously created ~20k DOM
  // nodes in one long task and froze the main thread ("page unresponsive"). We
  // instead reveal rows in small batches across animation frames, so no single
  // frame is expensive. Batch size shrinks as the column count grows to keep the
  // per-frame cost roughly constant regardless of how many parameters are shown.
  const totalRows = Math.max(
    filteredHomeTeam?.length || 0,
    filteredAwayTeam?.length || 0
  );
  const columnCount = visibleStatsMeta.length || 1;
  const [visibleRows, setVisibleRows] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (totalRows === 0) {
      setVisibleRows(0);
      return undefined;
    }

    const batch = Math.max(2, Math.floor(600 / columnCount));
    let current = Math.min(batch, totalRows);
    setVisibleRows(current);

    const step = () => {
      current = Math.min(current + batch, totalRows);
      setVisibleRows(current);
      rafRef.current = current < totalRows ? requestAnimationFrame(step) : null;
    };

    if (current < totalRows) {
      rafRef.current = requestAnimationFrame(step);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [totalRows, columnCount, items]);

  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = placeholder;
  };

  const toggleFavorite = async (player) => {
    try {
      const isCurrentlyFavorite = favoritePlayers[player.id] ?? false;

      if (isCurrentlyFavorite) {
        await $apiV1.delete(`/users/favorites/players/${player.id}/`);
      } else {
        await $apiV1.post(`/users/favorites/players/`, { player: player.id });
      }

      setFavoritePlayers((prev) => ({
        ...prev,
        [player.id]: !isCurrentlyFavorite,
      }));
    } catch (error) {
      console.error("Error toggling favorite status:", error);
    }
  };

  const handleSortTableHome = (parameterId) => {
    if (
      tableTypeSortingHome.type === "asc" &&
      parameterId === tableTypeSortingHome.param
    ) {
      setTableTypeSortingHome({ type: "desc", param: parameterId });
      return;
    }

    if (
      tableTypeSortingHome.type === "desc" &&
      parameterId === tableTypeSortingHome.param
    ) {
      setTableTypeSortingHome({ type: "default", param: parameterId });
      return;
    }

    setTableTypeSortingHome({ type: "asc", param: parameterId });
  };

  const handleSortTableAway = (parameterId) => {
    if (
      tableTypeSortingAway.type === "asc" &&
      parameterId === tableTypeSortingAway.param
    ) {
      setTableTypeSortingAway({ type: "desc", param: parameterId });
      return;
    }

    if (
      tableTypeSortingAway.type === "desc" &&
      parameterId === tableTypeSortingAway.param
    ) {
      setTableTypeSortingAway({ type: "default", param: parameterId });
      return;
    }

    setTableTypeSortingAway({ type: "asc", param: parameterId });
  };

  const sortPlayers = (players, sortState) => {
    const rows = [...(players || [])];

    if (!sortState?.param || sortState.type === "default") {
      return rows;
    }

    return rows.sort((a, b) => {
      const statA = getStat(a, sortState.param);
      const statB = getStat(b, sortState.param);

      if (statA && statB) {
        if (sortState.type === "asc") {
          return (statA.value || 0) - (statB.value || 0);
        }

        if (sortState.type === "desc") {
          return (statB.value || 0) - (statA.value || 0);
        }
      }

      if (statA) return -1;
      if (statB) return 1;

      return 0;
    });
  };

  const renderStatCells = (player) =>
    visibleStatsMeta.map((headerStat) => {
      const stat = getStat(player, headerStat.parameter_id);

      return (
        <td
          key={`${player.id}-${headerStat.parameter_id}`}
          className="statColumn"
          style={{ cursor: stat?.markers?.length ? "pointer" : "default" }}
          onClick={() => {
            if (stat?.markers?.length) {
              // Resolve the display name lazily on click instead of for every
              // cell during render, which was thousands of resolver calls.
              const displayName = getParameterDisplayName(stat || headerStat);
              openVideoList(
                stat.markers.join(","),
                `${displayName} - ${player?.name} ${player?.surname}`
              );
            }
          }}
        >
          {formatStatValue(stat?.value)}
        </td>
      );
    });

  const renderTable = ({ teamName, players, sortState, onSort }) => {
    const sortedPlayers = sortPlayers(players, sortState).slice(0, visibleRows);

    return (
      <PlayerTable>
        <Club style={{ marginTop: teamName === club?.away_team ? "20px" : undefined }}>
          <h2>{teamName}</h2>
        </Club>

        <PlayerStatsScrollContainer>
          <PlayerStatsSingleTable>
            <thead>
              <tr>
                <th className="playerColumn">
                  <PlayerColumnHeaderContent>
                    <PlayerMediaPlaceholder />
                    <HeaderLabelText>#</HeaderLabelText>
                    <HeaderLabelText>{l(319)}</HeaderLabelText>
                    <HeaderLabelText>{l(11)}</HeaderLabelText>
                  </PlayerColumnHeaderContent>
                </th>

                {visibleStatsMeta.map((stat) => {
                  const displayName = getParameterDisplayName(stat);

                  return (
                    <th
                      key={stat.parameter_id}
                      className="statColumn sortable"
                      title={displayName}
                      onClick={() => onSort(stat.parameter_id)}
                    >
                      <StatHeaderLabel>
                        {extractFirstCharacters(displayName)}
                        {sortState.param === stat.parameter_id ? (
                          sortState.type === "asc" ? (
                            <SortAscIcon />
                          ) : sortState.type === "desc" ? (
                            <SortDescIcon />
                          ) : (
                            <SortDefaultIcon />
                          )
                        ) : (
                          <SortDefaultIcon />
                        )}
                      </StatHeaderLabel>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {sortedPlayers.map((player) => {
                const isFavorite = favoritePlayers[player.id] ?? false;

                return (
                  <tr key={player.id}>
                    <td className="playerColumn">
                      <PlayerColumnRowContent>
                        <PlayerMediaContent>
                          <PlayerFavoriteIcon
                            src={isFavorite ? starOn : starOff}
                            alt="Favorite star"
                            onClick={() => toggleFavorite(player)}
                          />
                          <PlayerPhoto
                            src={player.photo ? player.photo : placeholder}
                            alt={player.display_name || `${player.name} ${player.surname}`}
                            onError={handleImageError}
                          />
                        </PlayerMediaContent>

                        <PlayerNumberText>{player.number}</PlayerNumberText>

                        <PlayerNameText
                          onClick={() => navigate(`/tournaments/player/${player.id}`)}
                          data-tooltip-id={`full-name-${teamName}-${player.id}`}
                          style={{ cursor: "pointer" }}
                        >
                          {player.display_name || `${player.name} ${player.surname}`}
                        </PlayerNameText>

                        <ReactTooltip
                          id={`full-name-${teamName}-${player.id}`}
                          place="bottom"
                          content={player.display_name || `${player.name} ${player.surname}`}
                        />

                        <PlayerPositionText>{player.positions?.[0]}</PlayerPositionText>
                      </PlayerColumnRowContent>
                    </td>

                    {renderStatCells(player)}
                  </tr>
                );
              })}
            </tbody>
          </PlayerStatsSingleTable>
        </PlayerStatsScrollContainer>
      </PlayerTable>
    );
  };

  if (loading) {
    const isLightTheme = localStorage.getItem("theme") === "light";

    return (
      <GlobalWrap>
        <PlayerStatsLoadingState>
          <Oval
            visible
            height="80"
            width="80"
            secondaryColor={isLightTheme ? "#4E5255" : "#CFDAE6"}
            color={isLightTheme ? "#2363F7" : "#4fa94d"}
            ariaLabel="oval-loading"
          />
        </PlayerStatsLoadingState>
      </GlobalWrap>
    );
  }

  return (
    <GlobalWrap>
      {renderTable({
        teamName: club?.home_team,
        players: filteredHomeTeam,
        sortState: tableTypeSortingHome,
        onSort: handleSortTableHome,
      })}
      {renderTable({
        teamName: club?.away_team,
        players: filteredAwayTeam,
        sortState: tableTypeSortingAway,
        onSort: handleSortTableAway,
      })}
    </GlobalWrap>
  );
};

export default PlayersStat;
